import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import InfoIcon from '@mui/icons-material/Info'
import { SvgIconComponent } from '@mui/icons-material'
import { DerivedStatus, Severity } from '../model/ISystemStatusMessage'

// Single source of truth for every severity colour/icon used across the banner,
// the table chips and the form preview. No severity colours are inlined at
// usage sites — read them from here. Literals come straight from the approved
// design token table (README "Palette — severities"); they have no equivalent
// in the MUI theme, so they are hard-coded here by design.
export interface SeverityToken {
  bg: string // banner / chip background
  rail: string // 4px banner rail
  icon: string // icon colour (Warning differs from its rail)
  title: string // banner title text
  meta: string // banner schedule / meta text
  body: string // expanded banner body text
  border: string // banner bottom border
  chipText: string // severity chip text (chip bg === bg)
  iconName: 'error' | 'warning' | 'info'
}

export const SEVERITY_TOKENS: Record<Severity, SeverityToken> = {
  [Severity.Critical]: {
    bg: '#FDEDEC',
    rail: '#C62828',
    icon: '#C62828',
    title: '#4A1513',
    meta: '#6C4744',
    body: '#3E2A29',
    border: '#F4C7C3',
    chipText: '#8A2320',
    iconName: 'error'
  },
  [Severity.Warning]: {
    bg: '#FFF6E8',
    rail: '#B26A00',
    icon: '#8A5200',
    title: '#4A3311',
    meta: '#6B5320',
    body: '#3E2A29',
    border: '#F5DDB0',
    chipText: '#8A5200',
    iconName: 'warning'
  },
  [Severity.Informational]: {
    bg: '#E9F4FB',
    rail: '#0269A8',
    icon: '#0269A8',
    title: '#123B52',
    meta: '#4A6675',
    body: '#123B52',
    border: '#BEDFF2',
    chipText: '#0269A8',
    iconName: 'info'
  }
}

// Table status chip colours (derived status, not stored).
export interface StatusChipToken {
  bg: string
  text: string
  border: string // 'none' or a css border value
}

export const STATUS_CHIP_TOKENS: Record<DerivedStatus, StatusChipToken> = {
  Active: { bg: '#E8F5E9', text: '#1E5B22', border: 'none' },
  Pending: { bg: '#EEF2F5', text: '#4A6675', border: 'none' },
  Expired: { bg: 'transparent', text: '#8B9AA5', border: '1px solid #DCE6ED' }
}

// Defensive: never return undefined (a render reading .iconName must not crash
// on an unexpected value). Falls back to the Informational tokens.
export const severityToken = (type: Severity): SeverityToken =>
  SEVERITY_TOKENS[type] ?? SEVERITY_TOKENS[Severity.Informational]

// The MUI icon component per token, so every severity surface (banner rows,
// the top-bar pill, table chips, form preview) renders the same glyph.
export const SEVERITY_ICONS: Record<
  SeverityToken['iconName'],
  SvgIconComponent
> = {
  error: ErrorIcon,
  warning: WarningIcon,
  info: InfoIcon
}

export const severityIcon = (type: Severity): SvgIconComponent =>
  SEVERITY_ICONS[severityToken(type).iconName]

// Shared react-intl message ids for the severity labels, so the banner, chips
// and form all read the same translations.
export const SEVERITY_LABEL_ID: Record<Severity, string> = {
  [Severity.Critical]: 'systemStatus.severity.critical',
  [Severity.Warning]: 'systemStatus.severity.warning',
  [Severity.Informational]: 'systemStatus.severity.informational'
}
